import {
    Dialog,
    DialogBody,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {ReferringSpecialist, ServiceUoc} from '../../types'
import {Controller, useForm} from 'react-hook-form'
import {
    CreateUpdateReferringSpecialistValidationSchema,
    CreateUpdateReferringSpecialistValidationSchemaType,
    SERVICE_UOC_MAX_LENGTH
} from '../../formSchemas'
import {zodResolver} from '@hookform/resolvers/zod'
import {useCreateReferringSpecialist} from '../../queries/useStoreReferringSpecialist'
import {errorHandler, raise} from '@/utilities/helpers'
import {useUpdateReferringSpecialist} from '../../queries/useUpdateReferringSpecialist'
import {StyledDeleteBudgetFooter, StyledDialogBody} from './style'
import {Button} from '@/components/ui/button/Button'
import {useTranslation} from '@/translations/i18n'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {InputText} from '@/components/commons/input-text/InputText'
import {InputSelectCombobox} from '@/components/commons/input-select-combobox/InputSelectCombobox'
import {useDialogContext} from '@ariakit/react'
import {toast} from '@/utilities/toast/toast'
import {usePermissions} from '@/features/auth/store/store'
import {WarningBanner} from '@/components/commons/warning-banner/WarningBanner'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {specialistTitleMapToLabel} from '@/features/referring-specialists/utils'

export const CreateUpdateReferringSpecialist = ({
    referringSpecialist,
    trigger,
    onMutateSpecialistSuccess
}: {
    referringSpecialist?: ReferringSpecialist
    trigger: React.ReactElement
    onMutateSpecialistSuccess?: (specialist: ReferringSpecialist) => void
}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()

    return (
        <DialogProvider>
            <DialogDisclosure render={trigger} />
            <Dialog width="600px" style={{overflowY: 'visible'}}>
                <DialogHeader>
                    <DialogHeading>
                        {referringSpecialist
                            ? t('referring_specialist:edit_specialist')
                            : t('referring_specialist:add_specialist')}
                    </DialogHeading>
                    <DialogDismiss />
                </DialogHeader>
                {permissions?.settings.write ? (
                    <>
                        <Form
                            referringSpecialist={referringSpecialist}
                            onMutateSpecialistSuccess={onMutateSpecialistSuccess}
                        />
                    </>
                ) : (
                    <CreateUpdateDeniedAccess />
                )}
            </Dialog>
        </DialogProvider>
    )
}

const Form = ({
    referringSpecialist,
    onMutateSpecialistSuccess
}: {
    referringSpecialist?: ReferringSpecialist
    onMutateSpecialistSuccess?: (specialist: ReferringSpecialist) => void
}) => {
    const {t} = useTranslation()
    const {setOpen} = useDialogContext() ?? raise('Dialog context not found')
    const {
        handleSubmit,
        register,
        control,
        formState: {errors}
    } = useForm<CreateUpdateReferringSpecialistValidationSchemaType>({
        resolver: zodResolver(CreateUpdateReferringSpecialistValidationSchema),
        defaultValues: referringSpecialist
            ? {
                  firstName: referringSpecialist.firstName,
                  lastName: referringSpecialist.lastName,
                  serviceUoc: referringSpecialist.serviceUoc,
                  email: referringSpecialist.email ?? '',
                  title: referringSpecialist.title ?? undefined
              }
            : undefined
    })
    const createReferringSpecialist = useCreateReferringSpecialist({
        onError: errorHandler,
        onSuccess: (data: ReferringSpecialist) => {
            setOpen(false)
            toast.success(t('referring_specialist:creation_success'))
            onMutateSpecialistSuccess?.(data)
        }
    })
    const updateReferringSpecialist = useUpdateReferringSpecialist({
        onError: errorHandler,
        onSuccess: (data: ReferringSpecialist) => {
            setOpen(false)
            toast.success(t('referring_specialist:update_success'))
            onMutateSpecialistSuccess?.(data)
        }
    })

    const onSubmit = handleSubmit(data => {
        if (referringSpecialist) {
            updateReferringSpecialist.mutate({payload: data, id: referringSpecialist.id})
        } else {
            createReferringSpecialist.mutate(data)
        }
    })

    return (
        <form onSubmit={onSubmit}>
            <StyledDialogBody gap={2} className="specialist-grid">
                <Controller
                    control={control}
                    name="title"
                    render={({field, fieldState: {error}}) => (
                        <InputSelect
                            label={t('referring_specialist:academic_title')}
                            optionsMap={specialistTitleMapToLabel}
                            placeholder={t('commons:choose')}
                            errorMessage={error?.message}
                            {...field}
                        />
                    )}
                />
                <InputText
                    type="text"
                    label={t('commons:last_name_label')}
                    {...register('lastName')}
                    errorMessage={errors.lastName?.message}
                    placeholder={t('commons:last_name_placeholder')}
                    style={{gridArea: 'last_name'}}
                />
                <InputText
                    type="text"
                    label={t('commons:first_name_label')}
                    {...register('firstName')}
                    errorMessage={errors.firstName?.message}
                    placeholder={t('commons:first_name_placeholder')}
                    style={{gridArea: 'first_name'}}
                />
                <Controller
                    control={control}
                    name="serviceUoc"
                    render={({field, fieldState: {error}}) => (
                        <InputSelectCombobox
                            label={t('referring_specialist:uoc_service')}
                            placeholder={t('commons:choose')}
                            options={ServiceUoc.options.map(option => ({
                                label: option.toString(),
                                value: option.toString()
                            }))}
                            allowCustomValue
                            customValueMaxLength={SERVICE_UOC_MAX_LENGTH}
                            errorMessage={error?.message}
                            onChange={field.onChange}
                            value={field.value}
                        />
                    )}
                />
                <InputText
                    type="text"
                    label={t('commons:email')}
                    {...register('email')}
                    errorMessage={errors.email?.message}
                    placeholder={t('commons:email_placeholder')}
                    style={{gridArea: 'email'}}
                />
            </StyledDialogBody>
            <StyledDeleteBudgetFooter>
                <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
                <Button
                    variant="primaryNeutral"
                    type="submit"
                    disabled={createReferringSpecialist.isPending || updateReferringSpecialist.isPending}
                >
                    {t('commons:confirm')}
                    {(createReferringSpecialist.isPending || updateReferringSpecialist.isPending) && <Spinner />}
                </Button>
            </StyledDeleteBudgetFooter>
        </form>
    )
}

const CreateUpdateDeniedAccess = () => {
    const {t} = useTranslation()
    return (
        <>
            <DialogBody>
                <WarningBanner title={t('errors:no_permission')} subtitle={t('errors:no_permission_specialist')} />
            </DialogBody>
            <StyledDeleteBudgetFooter>
                <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
            </StyledDeleteBudgetFooter>
        </>
    )
}
